/**
 * The chooser: `/` on a device in "choice" mode (Scan and type, D-019). Two
 * doors and nothing else — the classic scan page at `/scan`, or the typed
 * check-in at `/find`. No check-in happens here and no result card is ever
 * shown here; it is the kiosk's resting state, so nothing moves it on by
 * itself either (no idle timer — it *is* where idle lands).
 *
 * A scanner fired at it is not lost: the scanner hook is mounted here too, and
 * a badge it reads is forwarded to `/scan`, which checks it in, shows the
 * result and then comes back here (ScanScreen reads it from router state,
 * once; in this mode it returns to `/` after every result). A wedge scanner must
 * never go dead at the resting screen. A code read too slowly is forwarded the
 * same way, so the student sees the "scan again" card on `/scan` rather than
 * nothing at all.
 *
 * No hidden sink input: the hook listens on the document, and there is nothing
 * on this screen a scanner's keystrokes could land in and do harm. (Its focus
 * custody finds no input to hold, and so does nothing.)
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import GearLink from "../components/GearLink";
import { ConnectionPill, ExpiryBanner } from "../components/KioskChrome";
import { useDevice } from "../context/DeviceContext";
import { clockLine, useClock } from "../hooks/useClock";
import { useOnline } from "../hooks/useOnline";
import { useScannerInput } from "../hooks/useScannerInput";
import "./ScanScreen.css";
import "./ChooseScreen.css";

export default function ChooseScreen() {
  const navigate = useNavigate();
  const { device } = useDevice();
  const { online } = useOnline();
  const now = useClock();

  const handleScan = useCallback(
    (badge: string) => navigate("/scan", { replace: true, state: { badge } }),
    [navigate],
  );

  const handleSlowScan = useCallback(
    () => navigate("/scan", { replace: true, state: { slowScan: true } }),
    [navigate],
  );

  useScannerInput({ onScan: handleScan, onSlowScan: handleSlowScan });

  return (
    <div className="screen scan choose">
      <div className="zebra-field" aria-hidden="true" />

      <header className="topbar">
        <GearLink />
        <span className="scan-branch">{device?.branchName || "Unregistered"}</span>
        <span className="spacer" />
        <ConnectionPill online={online} />
      </header>

      <ExpiryBanner expiresAt={device?.expiresAt} />

      <main className="scan-stage choose-stage">
        <div className="idle idle--choice">
          <p className="eyebrow idle-eyebrow">Zebra Robotics · Check-in</p>

          <div className="idle-options">
            {/* Opens the classic single-purpose scan page. A scan fired at this
                screen gets there too, on its own (above). */}
            <button
              type="button"
              className="idle-option idle-option--scan"
              onClick={() => navigate("/scan", { replace: true })}
            >
              <span className="idle-target" aria-hidden="true">
                <span className="idle-sweep" />
              </span>
              {/* The system's hero pattern: white display type with one word in gold. */}
              <span className="idle-prompt display">
                Scan your
                <br />
                <span className="accent">badge.</span>
              </span>
            </button>

            <span className="idle-or" aria-hidden="true">
              or
            </span>

            {/* No `home` state: the find screen's home defaults to `/`, which is
                this screen — so its Back, its results and its idle timeout all
                hand the lobby back here. */}
            <button
              type="button"
              className="idle-option idle-option--type"
              onClick={() => navigate("/find", { replace: true })}
            >
              <KeysGlyph />
              <span className="idle-type-lede">No badge?</span>
              <span className="idle-type-prompt display">
                Type your
                <br />
                <span className="idle-type-mark">
                  name
                  <span className="idle-caret" />
                </span>
              </span>
              <span className="idle-type-go" aria-hidden="true">
                →
              </span>
            </button>
          </div>

          <p className="idle-clock">{clockLine(now)}</p>
        </div>
      </main>

      <footer className="scan-footer">
        <button type="button" className="btn-ghost" onClick={() => navigate("/today")}>
          Today&rsquo;s classes
        </button>
        <span className="spacer" />
        <img
          className="brand-lockup"
          src="/brand/logo-horizontal.png"
          alt="Zebra Robotics"
        />
      </footer>
    </div>
  );
}

/** Three rows of keys: the typing door's answer to the scan target. */
function KeysGlyph() {
  return (
    <span className="idle-keys" aria-hidden="true">
      <svg viewBox="0 0 64 40" width="64" height="40" fill="currentColor">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <rect key={`a${i}`} x={2 + i * 10} y={2} width="8" height="8" />
        ))}
        {[0, 1, 2, 3, 4].map((i) => (
          <rect key={`b${i}`} x={7 + i * 10} y={14} width="8" height="8" />
        ))}
        <rect x={12} y={26} width="40" height="8" />
      </svg>
    </span>
  );
}
